<script setup>
/**
 * Botão de "zerar o número de teste" do agente de IA da SuperTVGo.
 *
 * Repetir um teste do agente exige apagar o rastro anterior: o contato fica com
 * `lead_pos_teste` (e aí o bot cala), o Supabase já registrou que o telefone
 * usou o teste grátis (e aí não libera outro), e o agente ainda lembra da
 * conversa. Sem este botão, cada rodada de teste dependia de alguém mexer no
 * banco à mão.
 *
 * Só aparece para número que está na allowlist do agente, e quem decide é o
 * backend, em `GET /api/agente/atende/:telefone`. A lista não vem para cá de
 * propósito: este bundle é servido para qualquer visitante.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  contact: { type: Object, default: () => ({}) },
});

// A API da SuperTVGo vive em outro domínio que o painel; o CORS de lá libera
// só a origem do Chatwoot.
const API = 'https://api.supertvgo.com';

const telefone = computed(() =>
  String(
    props.contact?.phone_number || props.contact?.identifier || ''
  ).replace(/\D/g, '')
);

const { t } = useI18n();

const habilitado = ref(false);
const rodando = ref(false);
const confirmando = ref(false);
const resultado = ref('');

onMounted(async () => {
  if (!telefone.value) return;
  try {
    const res = await fetch(`${API}/api/agente/atende/${telefone.value}`);
    if (!res.ok) return;
    habilitado.value = Boolean((await res.json()).atende);
  } catch {
    // Backend fora do ar não pode quebrar o painel do contato: o botão
    // simplesmente não aparece.
    habilitado.value = false;
  }
});

const limpar = async () => {
  rodando.value = true;
  resultado.value = '';
  try {
    const res = await fetch(`${API}/api/agente/reset/${telefone.value}`, {
      method: 'POST',
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.erro || `HTTP ${res.status}`);

    const linhas = Object.values(json.postgres ?? {}).reduce(
      (a, b) => a + b,
      0
    );
    resultado.value = t('CONTACT_PANEL.AGENTE_RESET.DONE', { count: linhas });
  } catch (err) {
    resultado.value = t('CONTACT_PANEL.AGENTE_RESET.FAILED', {
      reason: err.message,
    });
  } finally {
    rodando.value = false;
    confirmando.value = false;
  }
};
</script>

<template>
  <div
    v-if="habilitado"
    class="flex flex-col w-full gap-1 pt-2 mt-2 border-t border-n-weak"
  >
    <button
      v-if="!confirmando"
      type="button"
      class="flex items-center gap-2 px-2 py-1.5 text-sm bg-transparent border-0 rounded-lg cursor-pointer text-n-ruby-11 hover:bg-n-alpha-2"
      :disabled="rodando"
      @click="confirmando = true"
    >
      <span class="i-lucide-eraser size-4" />
      <span>
        {{
          rodando
            ? $t('CONTACT_PANEL.AGENTE_RESET.RUNNING')
            : $t('CONTACT_PANEL.AGENTE_RESET.BUTTON')
        }}
      </span>
    </button>

    <template v-else>
      <p class="m-0 text-xs text-n-slate-11">
        {{ $t('CONTACT_PANEL.AGENTE_RESET.WARNING') }}
      </p>
      <div class="flex gap-2">
        <button
          type="button"
          class="px-2 py-1 text-sm text-white border-0 rounded-lg cursor-pointer bg-n-ruby-9"
          :disabled="rodando"
          @click="limpar"
        >
          {{ $t('CONTACT_PANEL.AGENTE_RESET.CONFIRM') }}
        </button>
        <button
          type="button"
          class="px-2 py-1 text-sm bg-transparent border-0 rounded-lg cursor-pointer text-n-slate-11"
          :disabled="rodando"
          @click="confirmando = false"
        >
          {{ $t('CONTACT_PANEL.AGENTE_RESET.CANCEL') }}
        </button>
      </div>
    </template>

    <p v-if="resultado" class="m-0 text-xs text-n-slate-11">
      {{ resultado }}
    </p>
  </div>
</template>
